import type { ThemeVariant } from '../../contract.js';
import { defaultLight, defaultDark } from '../default/variants.js';

/**
 * Dark Electric family — greyscale rework (post-C11).
 *
 * Original Driver Themes shipment paired an inherit-default light
 * variant with a bespoke pure-black dark variant, both with the
 * brand #21c163 accent threaded through chrome glows + halo
 * tints. Warren's production walk flagged that the green
 * saturated both variants too aggressively and broke the
 * "moving grey smoke on black" identity the OLED-friendly theme
 * was meant to convey. This rework drops brand green from accent
 * + chrome everywhere; only the literal `charging` token keeps
 * #21c163.
 *
 * Light variant: PROMOTED from inheriting Default light to a full
 * bespoke "soft dark" surface palette. Medium-dark grey
 * (#3A3A40 / #46464C / #52525A) — lighter than the bespoke dark
 * variant's #000000 but visibly darker than Default light's
 * #F7F5F0, giving the family a paired "soft dark by day, OLED
 * black by night" identity rather than a hard light/dark flip.
 * Accent → medium-light grey (#A0A0A0); widget + chrome tokens
 * adapted from Default DARK (not light) since the surface is now
 * dark-ish and the bright-surface glass recipes would muddy.
 *
 * Dark variant: bespoke pure-black chrome unchanged at the
 * bg/surface/text layer (#000000 / #0A0A0A / #141414). Accent →
 * medium grey (#8C8C8C); accentSoft, chargingGlow, and the card
 * glow stack all swap to grey-based recipes. `charging` stays
 * #21c163.
 */
export const darkElectricLight: ThemeVariant = {
  mode: 'light',
  tokens: {
    ...defaultLight.tokens,
    colors: {
      ...defaultLight.tokens.colors,
      // Bespoke "soft dark" palette. The `mode: 'light'` label is
      // for ThemeProvider routing (a driver who toggles to light
      // mode gets this variant); the actual chrome reads as a
      // muted dark grey because the family identity is "dark
      // electric" — the light variant is the daytime-friendly
      // companion, not a bright-surface theme.
      bg: '#3A3A40',
      surface: '#46464C',
      surface2: '#52525A',
      border: '#5A5A62',
      borderStrong: '#72727A',
      accent: '#A0A0A0',
      accentSoft: 'rgba(160, 160, 160, 0.14)',
      // Medium-grey accent → dark text on the CTA. WCAG contrast
      // against #A0A0A0: black ~6.2:1 (good); white ~2.6:1 (fail).
      // Dark text is the right call.
      onAccent: '#1A1A20',
      charging: '#21c163',
      chargingGlow: 'rgba(176, 176, 176, 0.45)',
      mute: '#72727A',
      text: '#E0E0E0',
      textMute: '#A0A0A8',
      // 3-stop grey card glow. Lighter-grey rim than the surface
      // tone so the glow reads as a soft lift rather than fading
      // into the chrome.
      cardGlow:
        '0 0 0 1px rgba(176, 176, 176, 0.20),'
        + ' 0 0 24px rgba(176, 176, 176, 0.18),'
        + ' 0 8px 32px rgba(176, 176, 176, 0.14)',
      cardGlowSubtle:
        '0 0 0 1px rgba(176, 176, 176, 0.10),'
        + ' 0 0 14px rgba(176, 176, 176, 0.08),'
        + ' 0 4px 16px rgba(176, 176, 176, 0.05)',
      // Widget + chrome glass — recipes adapted from Default DARK
      // rather than Default light, because the surface is dark-
      // ish (#46464C) and the bright-surface recipes inherited
      // from Default light's spread would produce washed-out
      // haze. Heavier drop shadows + dark translucent bgs match
      // the new surface luminance.
      widgetBg: 'rgba(70, 70, 76, 0.80)',
      widgetBorder: 'rgba(255, 255, 255, 0.08)',
      widgetShadow:
        '0 8px 24px rgba(0, 0, 0, 0.30),'
        + ' 0 0 0 1px rgba(176, 176, 176, 0.10)',
      chromeBg: 'rgba(70, 70, 76, 0.94)',
      chromeBorder: 'rgba(255, 255, 255, 0.06)',
      chromeShadow: '0 4px 16px rgba(0, 0, 0, 0.30)',
    },
  },
};

export const darkElectricDark: ThemeVariant = {
  mode: 'dark',
  tokens: {
    ...defaultDark.tokens,
    colors: {
      ...defaultDark.tokens.colors,
      bg: '#000000',
      surface: '#0A0A0A',
      surface2: '#141414',
      border: '#1F1F1F',
      borderStrong: '#2F2F2F',
      accent: '#8C8C8C',
      accentSoft: 'rgba(140, 140, 140, 0.18)',
      // Medium-grey accent → dark text on the CTA. WCAG contrast
      // against #8C8C8C: black ~5.0:1 (good); white ~3.4:1 (poor).
      // Dark text wins on readability against the grey button.
      onAccent: '#1A1A20',
      charging: '#21c163',
      chargingGlow: 'rgba(160, 160, 160, 0.55)',
      mute: '#5A5A5A',
      text: '#F0F0F0',
      textMute: '#9A9A9A',
      // 3-stop grey card glow on OLED-friendly black surface.
      // Medium-grey rim at decent alphas so the halo reads against
      // pure black without competing with the shader canvas. Same
      // shape as Default dark's recipe — only the colour swaps.
      cardGlow:
        '0 0 0 1px rgba(160, 160, 160, 0.22),'
        + ' 0 0 24px rgba(160, 160, 160, 0.28),'
        + ' 0 8px 32px rgba(160, 160, 160, 0.22)',
      cardGlowSubtle:
        '0 0 0 1px rgba(160, 160, 160, 0.10),'
        + ' 0 0 14px rgba(160, 160, 160, 0.14),'
        + ' 0 4px 12px rgba(160, 160, 160, 0.10)',
      // Widget + chrome glass — drop the green ring tint on the
      // shadow recipe for a faint grey ring at the same alpha;
      // bg/blur recipes unchanged (the OLED-near-black bg is
      // independent of accent hue).
      widgetBg: 'rgba(10, 10, 10, 0.78)',
      widgetBorder: 'rgba(255, 255, 255, 0.08)',
      widgetShadow:
        '0 12px 32px rgba(0, 0, 0, 0.55),'
        + ' 0 0 0 1px rgba(160, 160, 160, 0.10)',
      chromeBg: 'rgba(10, 10, 10, 0.94)',
      chromeBorder: 'rgba(255, 255, 255, 0.06)',
      chromeShadow: '0 8px 24px rgba(0, 0, 0, 0.60)',
    },
  },
};
